import { parseISO } from "date-fns"
import format from "date-fns/format"
import PropTypes from "prop-types"
import React from "react"

import LineItems from "src/components/LineItems/LineItems"

const formatDate = (date) => format(parseISO(date), "MMM d, yyyy")

const durationText = (quote) => {
  const start = formatDate(quote.startDate)
  if (quote.monthToMonth) return `Month to month starting ${start}`
  return `${start} - ${formatDate(quote.endDate)}`
}

const FOR_FULL_STAY = "for full stay"
const PER_MONTH = "per month"

const priceLine = (item) => ({
  type: "price",
  label: item.title,
  description: item.description,
  dates: item.dates,
  amount: item.price,
})

// Mirrors _contract_pricing_summary.slim's recurring-description suffixing:
// full-term billing ("billing" empty) gets " per month", installment billing
// gets " for full stay" — unless the description already says so.
const recurringDescription = (description, billing) => {
  if (!description) return description

  if (!description.includes("month") && billing.length > 0) {
    return description.includes("full stay")
      ? description
      : `${description} ${FOR_FULL_STAY}`
  }

  if (billing.length === 0) {
    return description.includes("month")
      ? description
      : `${description} ${PER_MONTH}`
  }

  return description
}

const recurringPriceLine = (billing) => (item) => ({
  ...priceLine(item),
  description: recurringDescription(item.description, billing),
})

const pricingGroup = (lineItems, title, items, mapItem = priceLine) => {
  if (!items || items.length === 0) return

  if (title) lineItems.push({ type: "section_heading", label: title })
  lineItems.push(...items.map(mapItem))
}

const taxLines = ({ salesTax, itemizedTaxes }) => {
  if (itemizedTaxes && itemizedTaxes.length > 0) {
    return itemizedTaxes.map((tax) => ({
      type: "tax",
      label: tax.displayName,
      description: tax.description,
      secondaryDescription: tax.monthlyDescription,
      amount: tax.amount,
    }))
  }

  if (salesTax && salesTax.price > 0) {
    return [
      {
        type: "tax",
        label: salesTax.label,
        description: salesTax.description,
        secondaryDescription: salesTax.monthlyDescription,
        amount: salesTax.price,
      },
    ]
  }

  return []
}

const buildLineItems = (pricingSummary) => {
  const { pricing, totalPrice, billing = [] } = pricingSummary
  const lineItems = []

  pricingGroup(
    lineItems,
    pricing.recurringTitle,
    pricing.recurring,
    recurringPriceLine(billing)
  )
  pricingGroup(lineItems, "One Time", pricing.oneTime)
  pricingGroup(lineItems, "Partial Stay", pricing.partial)

  lineItems.push(...taxLines(pricingSummary))

  lineItems.push({
    type: "total",
    label: totalPrice.title,
    amount: totalPrice.price,
  })

  return lineItems
}

const PurchaseSummary = ({ quote, pricingSummary, presentmentCurrency }) => (
  <div>
    <h4 className="mb-2 mt-0 font-semibold text-gray-700">Purchase Summary</h4>
    <div className="flex flex-col space-y-4 rounded-lg border border-gray-300 p-6">
      <div className="flex flex-col space-y-1">
        <span className="text-gray-700">Agreement Duration</span>
        <span className="font-semibold">{durationText(quote)}</span>
      </div>
      <hr className="m-0 border-gray-300" />
      <LineItems
        lineItems={buildLineItems(pricingSummary)}
        currency={presentmentCurrency}
      />
    </div>
  </div>
)

PurchaseSummary.propTypes = {
  quote: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string,
    monthToMonth: PropTypes.bool.isRequired,
  }).isRequired,
  pricingSummary: PropTypes.shape({
    pricing: PropTypes.shape({
      recurringTitle: PropTypes.string,
      recurring: PropTypes.array,
      oneTime: PropTypes.array,
      partial: PropTypes.array,
    }).isRequired,
    salesTax: PropTypes.shape({
      price: PropTypes.number,
      label: PropTypes.string,
      description: PropTypes.string,
      monthlyDescription: PropTypes.string,
    }),
    itemizedTaxes: PropTypes.array,
    billing: PropTypes.array,
    totalPrice: PropTypes.shape({
      title: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  presentmentCurrency: PropTypes.string.isRequired,
}

export default PurchaseSummary
